import React, { useState } from 'react';
import { CssBaseline, Container, Paper, Typography, Box, Grid } from '@mui/material';
import { useAuth } from 'react-oidc-context';
import { ThemeContextProvider, useTheme } from './contexts/ThemeContext';
import HighlightsSection from './components/HighlightsSection';
import ThoughtLeadershipSection from './components/ThoughtLeadershipSection';
import AdvancingOrgSection from './components/AdvancingOrgSection';
import GrowthAreaSection from './components/GrowthAreaSection';
import ICHighlightsSection from './components/ICHighlightsSection';
import ICThoughtLeadershipSection from './components/ICThoughtLeadershipSection';
import ICAdvancingOrgSection from './components/ICAdvancingOrgSection';
import DirectReportsFilter from './components/DirectReportsFilter';
import ThemeToggle from './components/ThemeToggle';
import { DirectReport } from './types';

/**
 * Authentication Flow (API Gateway + Cognito + Federate):
 * 1. User visits the app
 * 2. react-oidc-context checks for valid tokens
 * 3. If no tokens, redirects to Cognito
 * 4. Cognito redirects to Federate for SSO
 * 5. User logs in with Amazon credentials
 * 6. After login, user has JWT tokens
 * 7. All API calls include Authorization: Bearer <token>
 * 8. API Gateway validates token with Cognito Authorizer
 */

const AppContent: React.FC = () => {
  const auth = useAuth();
  const { mode } = useTheme();
  const [selectedDirectReport, setSelectedDirectReport] = useState<DirectReport | null>(null);
  const [isManager, setIsManager] = useState<boolean>(false);
  const [managerStatusLoaded, setManagerStatusLoaded] = useState<boolean>(false);
  const [userAlias, setUserAlias] = useState<string>('user');

  // Handle authentication state
  React.useEffect(() => {
    // If authentication is loading, wait
    if (auth.isLoading) {
      return;
    }

    // If user is not authenticated, trigger login
    if (!auth.isAuthenticated) {
      console.log('User not authenticated, redirecting to login...');
      auth.signinRedirect();
      return;
    }

    // User is authenticated, extract user info
    if (auth.user) {
      // Use preferred_username (user's login alias) instead of cognito:username (UUID)
      const username =
        auth.user.profile.preferred_username || auth.user.profile['cognito:alias'] || auth.user.profile.sub;
      console.log('User authenticated:', username);
      setUserAlias(username as string);
      (window as any).__currentUserAlias = username;
    }
    // Note: We don't include 'auth' to avoid infinite re-renders - only depend on specific properties
  }, [auth.isLoading, auth.isAuthenticated, auth.user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Validate manager status with backend on component mount
  // Backend gets user identity from API Gateway Cognito Authorizer
  React.useEffect(() => {
    // Don't call API until authenticated
    if (!auth.isAuthenticated || !auth.user) {
      return;
    }

    const validateManagerStatus = async () => {
      try {
        const response = await fetch('/api/v1/user/manager-status', {
          headers: {
            Authorization: `Bearer ${auth.user?.id_token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setIsManager(data.is_manager);
          // Backend returns user alias in response
          if (data.alias) {
            setUserAlias(data.alias);
            (window as any).__currentUserAlias = data.alias;
          }
        } else {
          console.error('Failed to validate manager status:', response.status);
          setIsManager(false);
        }
      } catch (error) {
        console.error('Error validating manager status:', error);
        setIsManager(false);
      } finally {
        setManagerStatusLoaded(true);
      }
    };

    validateManagerStatus();
  }, [auth.isAuthenticated, auth.user]);

  // Show loading while authentication is in progress
  if (auth.isLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 3, textAlign: 'center' }}>
        <Typography>Authenticating...</Typography>
      </Container>
    );
  }

  // Show loading while validating manager status
  if (!managerStatusLoaded) {
    return (
      <Container maxWidth="xl" sx={{ py: 3, textAlign: 'center' }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  const showManagerView = isManager;

  return (
    <>
      <CssBaseline />
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Paper
          elevation={3}
          sx={{
            p: 3,
            mb: 3,
            background:
              mode === 'dark'
                ? 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)'
                : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={3}>
            <Box>
              <Typography variant="h1" component="h1" gutterBottom>
                SA Talent Card
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Welcome, {userAlias} {showManagerView ? '(Manager)' : '(IC)'}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <ThemeToggle />
            </Box>
          </Box>
        </Paper>

        {/* Manager-specific components */}
        {showManagerView && (
          <DirectReportsFilter selectedReport={selectedDirectReport} onReportChange={setSelectedDirectReport} />
        )}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            {showManagerView ? (
              <HighlightsSection selectedAlias={selectedDirectReport?.alias} userAlias={userAlias} />
            ) : (
              <ICHighlightsSection />
            )}
          </Grid>
          <Grid item xs={12}>
            {showManagerView ? (
              <ThoughtLeadershipSection selectedAlias={selectedDirectReport?.alias} userAlias={userAlias} />
            ) : (
              <ICThoughtLeadershipSection />
            )}
          </Grid>
          <Grid item xs={12}>
            {showManagerView ? (
              <AdvancingOrgSection selectedAlias={selectedDirectReport?.alias} userAlias={userAlias} />
            ) : (
              <ICAdvancingOrgSection />
            )}
          </Grid>
          {showManagerView && (
            <Grid item xs={12}>
              <GrowthAreaSection selectedAlias={selectedDirectReport?.alias} userAlias={userAlias} />
            </Grid>
          )}
        </Grid>
      </Container>
    </>
  );
};

function App() {
  return (
    <ThemeContextProvider>
      <AppContent />
    </ThemeContextProvider>
  );
}

export default App;
